import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { apiClient } from '../lib/apiClient';
import { UnmappedSource } from '../types/api';
import { RefreshCw, CheckCircle, AlertCircle, ChevronDown, ChevronRight, Users, Building, Mail, ExternalLink } from 'lucide-react';
import SourceMappingRow from './SourceMappingRow';
import CreateCustomerModal from './CreateCustomerModal';

interface SourceMappingManagerProps {
  onMappingUpdate?: () => void;
}

interface GroupedSources {
  [key: string]: {
    sources: UnmappedSource[];
    label: string;
    icon: React.ReactNode;
    description: string;
  };
}

const SourceMappingManager: React.FC<SourceMappingManagerProps> = ({ onMappingUpdate }) => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState<{
    source: UnmappedSource;
    suggestedName: string;
  } | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Fetch unmapped sources
  const { 
    data: unmappedSourcesData, 
    isLoading: sourcesLoading, 
    error: sourcesError,
    refetch: refetchSources 
  } = useQuery({
    queryKey: ['unmappedSources'],
    queryFn: () => apiClient.getUnmappedSources(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Memoize unmapped sources to prevent unnecessary re-renders
  const unmappedSources = useMemo(() => 
    (unmappedSourcesData as UnmappedSource[]) || [], 
    [unmappedSourcesData]
  );

  // Fetch customers for mapping
  const { 
    data: customersData, 
    isLoading: customersLoading,
    error: customersError
  } = useQuery({
    queryKey: ['customers', 'all'],
    queryFn: () => apiClient.getCustomers({ limit: 1000 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const customers = (customersData as any)?.customers || [];

  // Map source to existing customer mutation
  const mapSourceMutation = useMutation({
    mutationFn: async ({ source, customerId }: { source: UnmappedSource; customerId: string }) => {
      return apiClient.mapSourceToCustomer(source.source_type, source.source_id, customerId);
    },
    onSuccess: () => {
      // Invalidate and refetch unmapped sources
      queryClient.invalidateQueries({ queryKey: ['unmappedSources'] });
      onMappingUpdate?.();
    },
    onError: (error) => {
      console.error('Failed to map source:', error);
    }
  });

  // Exclude channel mutation
  const excludeChannelMutation = useMutation({
    mutationFn: async (channelId: string) => {
      return apiClient.excludeSlackChannel(channelId);
    },
    onSuccess: () => {
      // Invalidate and refetch unmapped sources
      queryClient.invalidateQueries({ queryKey: ['unmappedSources'] });
      onMappingUpdate?.();
    },
    onError: (error) => {
      console.error('Failed to exclude channel:', error);
    }
  });

  // Group sources by category based on confidence and patterns
  const groupedSources = useMemo(() => {
    const groups: GroupedSources = {
      'high-confidence': {
        sources: [],
        label: 'High Confidence Account Channels',
        icon: <Users className="w-5 h-5 text-green-600" />,
        description: 'Channels with strong account indicators (≥80% confidence)'
      },
      'medium-confidence': {
        sources: [],
        label: 'Potential Account Channels',
        icon: <Building className="w-5 h-5 text-yellow-600" />,
        description: 'Channels that might be account-related (40-79% confidence)'
      },
      'low-confidence': {
        sources: [],
        label: 'Review Required',
        icon: <AlertCircle className="w-5 h-5 text-orange-600" />,
        description: 'Channels needing manual review (20-39% confidence)'
      },
      'internal-channels': {
        sources: [],
        label: 'Internal/System Channels',
        icon: <ExternalLink className="w-5 h-5 text-gray-600" />,
        description: 'Likely internal channels (<20% confidence)'
      },
      'other-sources': {
        sources: [],
        label: 'Other Data Sources',
        icon: <Mail className="w-5 h-5 text-blue-600" />,
        description: 'CRM accounts, email domains, and other sources'
      }
    };

    unmappedSources.forEach(source => {
      const confidence = source.confidence_score || 0;
      
      // Categorize non-Slack sources separately
      if (source.source_type !== 'slack_channel') {
        groups['other-sources'].sources.push(source);
        return;
      }

      // Categorize Slack channels by confidence
      if (confidence >= 80) {
        groups['high-confidence'].sources.push(source);
      } else if (confidence >= 40) {
        groups['medium-confidence'].sources.push(source);
      } else if (confidence >= 20) {
        groups['low-confidence'].sources.push(source);
      } else {
        groups['internal-channels'].sources.push(source);
      }
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].sources.length === 0) {
        delete groups[key];
      }
    });

    return groups;
  }, [unmappedSources]);

  const handleMapToExistingCustomer = async (source: UnmappedSource, customerId: string) => {
    await mapSourceMutation.mutateAsync({ source, customerId });
  };

  const handleCreateNewCustomer = (source: UnmappedSource) => {
    setShowCreateModal({
      source,
      suggestedName: source.suggested_customer_name || source.source_name
    });
  };

  const handleExcludeSource = async (source: UnmappedSource) => {
    if (source.source_type === 'slack_channel') {
      await excludeChannelMutation.mutateAsync(source.source_id);
    }
  };

  const handleModalSuccess = () => {
    setShowCreateModal(null);
    // Invalidate and refetch unmapped sources
    queryClient.invalidateQueries({ queryKey: ['unmappedSources'] });
    onMappingUpdate?.();
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const totalSources = Object.values(groupedSources).reduce((sum, group) => sum + group.sources.length, 0);

  if (sourcesLoading || customersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Source Mapping</h2>
          <p className="text-muted-foreground">
            Map unmapped data sources to accounts for better insights.
          </p>
        </div>
        
        <Button onClick={() => refetchSources()} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error State */}
      {sourcesError && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between text-destructive">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span>Failed to load unmapped sources. Please try again.</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => refetchSources()}
                className="text-destructive border-destructive hover:bg-destructive/10"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grouped Unmapped Sources */}
      <Card className="overflow-visible">
        <CardHeader>
          <CardTitle>
            Unmapped Sources ({totalSources})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-visible">
          {totalSources === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">All sources mapped!</h3>
              <p className="text-muted-foreground">
                All discovered data sources have been mapped to accounts.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedSources).map(([groupKey, group]) => (
                <div key={groupKey} className="border border-border rounded-lg overflow-visible">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full px-4 py-3 bg-muted/50 hover:bg-muted transition-colors flex items-center justify-between text-left"
                  >
                    <div className="flex items-center space-x-3">
                      {group.icon}
                      <div>
                        <h3 className="font-medium text-foreground">{group.label}</h3>
                        <p className="text-sm text-muted-foreground">{group.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-muted-foreground bg-background px-2 py-1 rounded-full">
                        {group.sources.length}
                      </span>
                      {expandedGroups.has(groupKey) ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {/* Group Content */}
                  {expandedGroups.has(groupKey) && (
                    <div className="p-4 space-y-3 bg-background overflow-visible">
                      {group.sources.map((source) => (
                        <SourceMappingRow
                          key={`${source.source_type}-${source.source_id}`}
                          source={source}
                          customers={customers}
                          onMapToExisting={handleMapToExistingCustomer}
                          onCreateNew={handleCreateNewCustomer}
                          onExclude={handleExcludeSource}
                          loading={mapSourceMutation.isPending || excludeChannelMutation.isPending}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Customer Modal */}
      {showCreateModal && (
        <CreateCustomerModal
          source={showCreateModal.source}
          suggestedName={showCreateModal.suggestedName}
          onClose={() => setShowCreateModal(null)}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};



export default SourceMappingManager;
